import { createTheme } from '@mui/material/styles';

const brand = {
  navy: '#0c1e4a',
  blue: '#2b7fff',
  blueDark: '#1a5fd4',
  blueLight: '#e8f2ff',
};

const lightPalette = {
  mode: 'light',
  primary: {
    main: brand.blue,
    dark: brand.blueDark,
    light: brand.blueLight,
    contrastText: '#ffffff',
  },
  secondary: {
    main: brand.navy,
    contrastText: '#ffffff',
  },
  error: { main: '#dc2626' },
  warning: { main: '#d97706' },
  success: { main: '#16a34a' },
  info: { main: '#6366f1' },
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
  },
  text: {
    primary: '#0f172a',
    secondary: '#64748b',
  },
  divider: '#e2e8f0',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: brand.blue,
    dark: brand.blueDark,
    light: brand.blueLight,
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#93c5fd',
    contrastText: brand.navy,
  },
  error: { main: '#dc2626' },
  warning: { main: '#d97706' },
  success: { main: '#16a34a' },
  info: { main: '#6366f1' },
  background: {
    default: '#0f1419',
    paper: '#1a222d',
  },
  text: {
    primary: '#f1f5f9',
    secondary: '#b8c5d6',
  },
  divider: '#2a3544',
};

const sharedTypography = {
  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
  h4: { fontWeight: 600, letterSpacing: '-0.02em' },
  h5: { fontWeight: 600, letterSpacing: '-0.01em' },
  h6: { fontWeight: 600, letterSpacing: '-0.01em' },
  subtitle1: { fontWeight: 500 },
  button: { textTransform: 'none', fontWeight: 500 },
};

const componentOverrides = (palette) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: palette.background.default,
        color: palette.text.primary,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: palette.background.paper,
        backgroundImage: 'none',
        border: `1px solid ${palette.divider}`,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        color: palette.text.primary,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        color: palette.text.primary,
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: '0.625rem',
        textTransform: 'none',
        fontWeight: 500,
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: brand.blueDark,
        },
      },
      outlined: {
        borderColor: palette.divider,
        color: palette.text.primary,
        '&:hover': {
          borderColor: palette.mode === 'dark' ? '#93c5fd' : brand.blue,
          backgroundColor:
            palette.mode === 'dark'
              ? 'rgba(43, 127, 255, 0.12)'
              : 'rgba(43, 127, 255, 0.04)',
        },
      },
      text: {
        color: brand.blue,
      },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: '0.875rem',
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '0.625rem',
          backgroundColor: palette.mode === 'light' ? '#ffffff' : palette.background.paper,
          color: palette.text.primary,
          '& fieldset': {
            borderColor: palette.divider,
          },
          '&:hover fieldset': {
            borderColor: palette.mode === 'dark' ? '#93c5fd' : brand.blue,
          },
          '&.Mui-focused fieldset': {
            borderColor: brand.blue,
          },
        },
        '& .MuiInputLabel-root': {
          color: palette.text.secondary,
          '&.Mui-focused': {
            color: palette.mode === 'dark' ? '#93c5fd' : brand.blue,
          },
        },
        '& .MuiInputBase-input::placeholder': {
          color: palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: '0.5rem', fontWeight: 500 },
      outlined: { borderColor: palette.divider },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { backgroundColor: brand.blue, height: 3, borderRadius: 3 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        color: palette.text.secondary,
        '&.Mui-selected': {
          color: palette.mode === 'dark' ? '#93c5fd' : brand.navy,
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: '0.625rem',
        color: palette.text.primary,
        '&.Mui-selected': {
          backgroundColor: palette.mode === 'dark' ? '#1a3052' : brand.blueLight,
          color: palette.mode === 'dark' ? '#93c5fd' : brand.navy,
          '&:hover': {
            backgroundColor: palette.mode === 'dark' ? '#1a3052' : brand.blueLight,
          },
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: palette.divider,
      },
      wrapper: {
        color: palette.text.secondary,
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: '0.625rem' },
    },
  },
});

export function createInterlinkedTheme(mode = 'light') {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  return createTheme({
    palette,
    typography: sharedTypography,
    shape: { borderRadius: 14 },
    components: componentOverrides(palette),
  });
}

export { brand };
